import { FC, ReactNode } from "react";
import "./ButtonGroup.scss";

interface ButtonProps {
  /**
   * The label to display on the button.
   */
  label?: string;
  /**
   * Function to be called when the button is clicked.
   */
  onClick?: () => void;
  /**
   * Optional icon to display alongside the button label.
   */
  icon?: ReactNode;
  /**
   * Whether the button is active (selected) or not.
   */
  isActive?: boolean;
}

interface ButtonGroupProps {
  /**
   * An array of button configurations.
   * Each object should adhere to the ButtonProps interface.
   */
  buttons: ButtonProps[];
  /**
   * Whether the buttons should take the full width of the parent and share it evenly.
   */
  fullWidth?: boolean;
  size?: "sm" | "md";
}

const ButtonGroup: FC<ButtonGroupProps> = function ({ size = "md", buttons, fullWidth = false }) {
  return (
    <div className={`btn-group ${fullWidth ? "btn-group-full-width" : ""}`}>
      {buttons.map(({ label, onClick, icon, isActive }, index) => {
        const classes: { [key: string]: boolean } = {
          "btn-group-btn": true,
          "btn-group-btn-no-label": !label,
          "btn-group-btn_active": isActive || false,
          "btn-group-btn-sm": size === "sm",
          "btn-group-btn-md": size === "md",
        };
        const btnClass = Object.keys(classes)
          .filter((key) => classes[key as keyof typeof classes])
          .join(" ");
        return (
          <button key={index} className={btnClass} onClick={onClick} type="button">
            {icon && <span className="btn-group-btn-icon">{icon}</span>}
            {label}
          </button>
        );
      })}
    </div>
  );
};

ButtonGroup.defaultProps = {
  fullWidth: false,
  size: "md" as "sm" | "md",
};

export default ButtonGroup;
